import { Platform } from 'react-native';
import { createURL } from 'expo-linking';
import { HOST } from '@api/constant';

/** Web OAuth return path — must match provider dashboards + deep linking where applicable. */
export const INTEGRATIONS_WEB_OAUTH_PATH = '/universal/integrations';

export function getIntegrationOAuthWebRedirectUri(): string {
	return createURL(INTEGRATIONS_WEB_OAUTH_PATH);
}

/**
 * Native OAuth redirect: `{API origin}/app-redirect`.
 * Uses sync `HOST` from `@api/constant` (same origin family as API calls).
 * Providers and backend must allowlist this exact URL per environment.
 */
export function getIntegrationOAuthNativeRedirectUri(): string {
	const base = (HOST || '').replace(/\/+$/, '');
	if (!base) {
		return 'https://api.smartsaas.pro/app-redirect';
	}
	return `${base}/app-redirect`;
}

export function getIntegrationOAuthRedirectUri(): string {
	return Platform.OS === 'web'
		? getIntegrationOAuthWebRedirectUri()
		: getIntegrationOAuthNativeRedirectUri();
}

/** For Meta embedded login: redirect_uri query param is often URL-encoded separately. */
export function getIntegrationOAuthNativeRedirectUriEncoded(): string {
	return encodeURIComponent(getIntegrationOAuthNativeRedirectUri());
}

export function getIntegrationOAuthWebRedirectUriEncoded(): string {
	return encodeURIComponent(getIntegrationOAuthWebRedirectUri());
}

/**
 * HTTPS return/refresh URL for Stripe Connect Account Links (native + API body).
 * Must match backend `resolveStripeOnboardingReturnUrl` when using the same origin
 * (see `PUBLIC_PROFILE_PAGE_HOST` / `BASE_URL` on server). Used as the second argument
 * to `WebBrowser.openAuthSessionAsync` so the in-app browser closes when Stripe redirects here.
 *
 * Override with `EXPO_PUBLIC_STRIPE_CONNECT_RETURN_ORIGIN` (e.g. `https://staging.example.com`)
 * if it differs from production.
 */
export function getStripeConnectAccountLinkReturnUrl(): string {
	const raw =
		(typeof process !== 'undefined' &&
			process.env.EXPO_PUBLIC_STRIPE_CONNECT_RETURN_ORIGIN?.trim()) ||
		'https://smartsaas.pro';
	const stripped = raw.replace(/^['"]|['"]$/g, '').trim();
	const base = stripped.replace(/\/+$/, '');
	return `${base}${INTEGRATIONS_WEB_OAUTH_PATH}`;
}
